import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, RotateCw, ArrowLeft, ShieldCheck } from 'lucide-react';
import PassportViewer from '../components/Passport/PassportViewer';
import RecruiterGuard from '../components/Auth/RecruiterGuard';
import { RecruiterGateCard, RecruiterPageShell } from '../components/Recruiter/RecruiterStates';
import { useAuth } from '../hooks/useAuth';
import { rememberPostAuthRedirect } from '../lib/postAuthRedirect';
import { resolveShareLink, type ResolvedShareLink, type ShareStatus } from '../api/passportShare';

/**
 * Passport share deep-link (/p/:token).
 *
 * The whole route is wrapped in RecruiterGuard, so by the time this renders the
 * viewer is an authenticated recruiter (logged-out / explorer visitors are
 * bounced by the guard). We resolve the token to a cv_id + candidate prefs and
 * render the same full recruiter passport view a recruiter sees from search —
 * incl. Intro and Status. The candidate's live visibility is re-checked on the
 * backend on every open, so an invisible candidate shows "unavailable".
 */

const MessageCard: React.FC<{
  title: string;
  body: string;
  ctaLabel: string;
  onRetry?: () => void;
}> = ({ title, body, ctaLabel, onRetry }) => {
  const navigate = useNavigate();
  return (
    <div className="mx-auto mt-16 max-w-md rounded-3xl border border-slate-200/70 bg-white p-8 text-center shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
      <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
      <button
        type="button"
        onClick={onRetry ? onRetry : () => navigate('/?tab=hire')}
        className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-pink-600"
      >
        {onRetry ? <RotateCw className="h-4 w-4" /> : <ArrowLeft className="h-4 w-4" />}
        {ctaLabel}
      </button>
    </div>
  );
};

/** Purpose-specific signed-out gate — only used on /p/:token. */
const ShareLinkLoggedOutGate: React.FC = () => {
  const { t } = useTranslation('passportShare');
  const location = useLocation();
  const returnTo = location.pathname + location.search;
  const ret = encodeURIComponent(returnTo);
  const loginHref = `/login?mode=recruiter&return=${ret}`;
  const signupHref = `/recruiter-signup?return=${ret}`;

  const goAuth = (href: string) => {
    rememberPostAuthRedirect(returnTo);
    window.location.href = href;
  };

  return (
    <RecruiterPageShell noPadding contentClassName="flex min-h-screen items-center justify-center px-4 py-12">
      <RecruiterGateCard
        icon={<ShieldCheck className="size-6 text-white" />}
        title={t('gate.title')}
        description={t('gate.body')}
      >
        <a
          href={loginHref}
          onClick={(e) => {
            e.preventDefault();
            goAuth(loginHref);
          }}
          className="mt-8 flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 py-3 text-sm font-semibold text-white shadow-lg shadow-pink-500/20 transition-all hover:shadow-xl hover:shadow-pink-500/25"
        >
          {t('gate.login')}
        </a>
        <a
          href={signupHref}
          onClick={(e) => {
            e.preventDefault();
            goAuth(signupHref);
          }}
          className="mt-3 block text-center text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
        >
          {t('gate.signup')}
        </a>
      </RecruiterGateCard>
    </RecruiterPageShell>
  );
};

const ShareLandingInner: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('passportShare');
  const { apiCall } = useAuth();

  const [result, setResult] = useState<ResolvedShareLink | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!token) {
      setResult({ status: 'invalid' });
      setLoading(false);
      return;
    }
    setLoading(true);
    const res = await resolveShareLink(apiCall, token);
    setResult(res);
    setLoading(false);
  }, [token, apiCall]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !result) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center text-slate-500">
        <Loader2 className="mb-3 h-7 w-7 animate-spin" />
        <p className="text-sm">{t('loading')}</p>
      </div>
    );
  }

  const status: ShareStatus = result.status;

  if (status === 'unavailable') {
    return (
      <MessageCard
        title={t('unavailable.title')}
        body={t('unavailable.body')}
        ctaLabel={t('unavailable.cta')}
      />
    );
  }

  if (status === 'expired' || status === 'invalid') {
    return (
      <MessageCard title={t('invalid.title')} body={t('invalid.body')} ctaLabel={t('invalid.cta')} />
    );
  }

  if (status !== 'ok' || !result.cvId) {
    return (
      <MessageCard
        title={t('error.title')}
        body={t('error.body')}
        ctaLabel={t('error.retry')}
        onRetry={load}
      />
    );
  }

  return (
    <div className="mx-auto flex h-screen max-w-4xl flex-col px-3 py-4 sm:px-6 sm:py-6">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {t('brand')}
      </p>
      <div className="min-h-0 flex-1 overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <PassportViewer
          cvId={result.cvId}
          audience="recruiter"
          candidateId={result.candidateId}
          recruiterPrefs={result.prefs}
        />
      </div>
    </div>
  );
};

const PassportShareLanding: React.FC = () => (
  <RecruiterGuard loadingIcon={ShieldCheck} loggedOutFallback={<ShareLinkLoggedOutGate />}>
    <RecruiterPageShell noPadding contentClassName="min-h-screen bg-slate-50/40">
      <ShareLandingInner />
    </RecruiterPageShell>
  </RecruiterGuard>
);

export default PassportShareLanding;
